<template>
  <div class="home">
    <h2>Testing goals</h2>
    <b-button @click="raisePopup({ target: $event.target })" variant="primary">
      Add new
    </b-button>

    <!-- Filtering -->
    <b-row>
      <b-col md="12" class="my-1">
        <b-form-group label-cols-sm="1" label="Filter" class="mb-0">
          <b-input-group>
            <b-form-input
              v-model="filter.goalName"
              placeholder="by Goal Name"
              @keypress.enter="forceFilter"
            />

            <b-input-group-append>
              <b-button
                :disabled="!filter.goalName"
                @click="
                  filter = {};
                  forceFilter();
                "
                >Clear</b-button
              >
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Info modal -->
    <b-modal
      id="goalsModal"
      v-bind:title="this.isEdit ? 'Edit goal' : 'New goal'"
      @ok="goalFormSubmit"
      :ok-disabled="!this.form.goalName"
    >
      <b-form>
        <input id="newGoalAbtestingInput0" type="hidden" v-model="form.id" />
        <b-form-group
          id="newGoalAbtestingInputGroup1"
          label="Goal name:"
          label-for="newGoalAbtestingInputGroup1"
        >
          <b-form-input
            id="newGoalAbtestingInput1"
            type="text"
            v-model="form.goalName"
            placeholder="Enter goal name"
          />
        </b-form-group>

        <b-form-group
          id="newGoalAbtestingInputGroup4"
          label="Description:"
          label-for="newGoalAbtestingInput4"
        >
          <b-form-textarea
            id="newGoalAbtestingInput6"
            v-model="form.description"
            placeholder="Enter description..."
            rows="3"
            max-rows="6"
          ></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="goalsTable"
        class="goalsTable"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>
          <b-button
            size="sm"
            class="m-1"
            variant="warning"
            @click="
              raisePopup({
                target: $event.target,
                isEdit: true,
                goal: row.item
              })
            "
          >
            ✎
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              deleteGoal({
                target: $event.target,
                goal: row.item
              })
            "
          >
            X
          </b-button>
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

const originalForm = {
  id: null,
  goalName: null,
  description: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [5, 20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: {
        goalName: ''
      },
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'goalName',
          label: 'Goal Name',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'description',
          label: 'Description',
          sortable: false,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.goalsAbtesting.count;
    }
  },
  methods: {
    forceFilter() {
      this.$refs.goalsTable.refresh();
    },
    raisePopup({ target, isEdit = false, goal = null }) {
      this.isEdit = isEdit;
      if (goal && isEdit) {
        this.form = Object.keys(originalForm).reduce(
          (acc, current) =>
            goal[current]
              ? {
                  ...acc,
                  [current]: String(goal[current])
                }
              : acc,
          {}
        );
      } else {
        this.goalFormReset();
      }
      this.$root.$emit('bv::show::modal', 'goalsModal', target);
    },
    deleteGoal({ goal = null }) {
      if (!goal) {
        return;
      }
      const promise = this.$store.dispatch('deleteGoalAbtesting', goal.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.goalsTable.refresh();
      });
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    items(ctx) {
      const currentFilter = Object.keys(this.filter).reduce(
        (acc, curr) =>
          this.filter[curr] ? { ...acc, [curr]: this.filter[curr] } : acc,
        {}
      );

      const promise = this.$store.dispatch('getGoalsAbtesting', {
        ctx,
        filter: currentFilter
      });

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        return this.$store.state.goalsAbtesting.rows || [];
      });
    },
    goalFormReset() {
      this.form = { ...originalForm };
    },
    async goalFormSubmit(event) {
      if (!this.isEdit) {
        await this.$store.dispatch('addGoalAbtesting', {
          goal: this.form,
          event
        });
      } else {
        await this.$store.dispatch('editGoalAbtesting', {
          goal: this.form,
          event
        });
      }

      this.goalFormReset();
      this.$refs.goalsTable.refresh();
    }
  },
  mounted() {
    this.$store.dispatch('getVersions');
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.goalsTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
